import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2Icon, ShieldCheckIcon, StarIcon, HomeIcon, AppWindowIcon, LayersIcon, DoorOpenIcon, ChevronDownIcon } from 'lucide-react';
import { UniversalWindowsDirectQuoteForm } from './UniversalWindowsDirectQuoteForm';
interface UniversalWindowsDirectLandingPageProps {
  initialService?: string;
  onClose: () => void;
}
export function UniversalWindowsDirectLandingPage({
  initialService,
  onClose
}: UniversalWindowsDirectLandingPageProps) {
  const [showForm, setShowForm] = useState(false);
  const [selectedService, setSelectedService] = useState<string>(initialService || '');
  const [zipCode, setZipCode] = useState('');
  const [isValidZip, setIsValidZip] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (initialService) {
      setSelectedService(initialService);
    }
  }, [initialService]);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleZipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipCode(val);
    setIsValidZip(val.length === 5);
  };
  const handleContinue = () => {
    if (isValidZip && selectedService) {
      setShowForm(true);
    }
  };
  if (showForm) {
    return <UniversalWindowsDirectQuoteForm initialZip={zipCode} serviceSlug={selectedService} onClose={onClose} data-id="element-2986" />;
  }
  const services = [{
    id: 'windows',
    name: 'Windows',
    icon: <AppWindowIcon className="w-6 h-6" data-id="element-2987" />,
    desc: 'Energy-efficient replacement windows'
  }, {
    id: 'siding',
    name: 'Siding',
    icon: <LayersIcon className="w-6 h-6" data-id="element-2988" />,
    desc: 'Premium vinyl siding'
  }, {
    id: 'gutters',
    name: 'Gutters',
    icon: <HomeIcon className="w-6 h-6" data-id="element-2989" />,
    desc: 'Gutter protection systems'
  }, {
    id: 'doors',
    name: 'Doors',
    icon: <DoorOpenIcon className="w-6 h-6" data-id="element-2990" />,
    desc: 'Entry & patio doors'
  }];
  const serviceLabel = services.find(s => s.id === selectedService)?.name || '';
  const heroImages: Record<string, string> = {
    windows: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80',
    siding: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1920&q=80',
    gutters: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?w=1920&q=80',
    doors: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80'
  };
  const heroImage = heroImages[selectedService] || 'https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=1920&q=80';
  const heroHeadlines: Record<string, string> = {
    windows: 'Premium Replacement Windows, Expertly Installed',
    siding: 'Transform Your Home with Premium Siding',
    gutters: 'Never Clean Your Gutters Again',
    doors: 'Beautiful, Secure Entry & Patio Doors'
  };
  const heroHeadline = initialService ? heroHeadlines[initialService] || 'Transform Your Home With Universal Windows Direct' : 'Transform Your Home With Universal Windows Direct';
  return <div className="min-h-screen bg-[#FAFAFA] font-sans" data-id="element-2991">
      {/* Header */}
      <header className="bg-white py-4 px-6 md:px-12 flex justify-center items-center shadow-sm relative z-20" data-id="element-2992">
        <div className="flex flex-col items-center" data-id="element-2993">
          <div className="flex items-baseline gap-2 mb-1" data-id="element-2994">
            <span className="text-2xl md:text-3xl font-black text-[#00529B] tracking-tight" data-id="element-2995">
              UNIVERSAL WINDOWS DIRECT
            </span>
          </div>
          <span className="text-[10px] md:text-sm font-semibold text-gray-500" data-id="element-2996">
            Windows &bull; Siding &bull; Gutters &bull; Doors
          </span>
        </div>
      </header>

      {/* Offer Banner */}
      <section className="bg-[#00529B] py-4 md:py-6 border-t border-blue-900" data-id="element-2997">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-2998">
          <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight" data-id="element-2999">
            Buy 1 Get 1 FREE on Windows & Doors*
          </h2>
          <p className="text-blue-100 text-sm mt-1" data-id="element-3000">
            Plus 24 Months No Interest Financing Available
          </p>
        </div>
      </section>

      {/* Hero Section */}
      <section className="relative pt-12 pb-24 md:pt-20 md:pb-32 overflow-hidden" data-id="element-3001">
        <div className="absolute inset-0 z-0" data-id="element-3002">
          <img src={heroImage} alt="Home improvement" className="w-full h-full object-cover" data-id="element-3003" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent" data-id="element-3004" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-3005">
          <div className="flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-3006">
            {/* Left Content */}
            <motion.div className="flex-1 text-white" initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-3007">
              <h1 className="text-4xl md:text-6xl font-extrabold tracking-tight mb-4 leading-tight drop-shadow-lg" data-id="element-3008">
                {heroHeadline}
              </h1>
              <p className="text-lg md:text-xl font-medium text-gray-200 drop-shadow-md max-w-lg" data-id="element-3009">
                Get a free estimate from one of America's fastest-growing home
                improvement companies.
              </p>
            </motion.div>

            {/* Right Form Card */}
            <motion.div className="w-full max-w-md" initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} data-id="element-3010">
              <div className="bg-white rounded-2xl shadow-2xl p-8 border border-gray-100" data-id="element-3011">
                <h2 className="text-2xl font-bold text-[#00529B] text-center mb-6" data-id="element-3012">
                  {initialService ? 'Enter Your Zip Code To Start' : 'Select a Service & Enter Your Zip'}
                </h2>

                {/* Service dropdown (only if no initialService) */}
                {!initialService && <div className="mb-6 relative" ref={dropdownRef} data-id="element-3013">
                    <button onClick={() => setDropdownOpen(!dropdownOpen)} className={`w-full flex items-center justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200 bg-blue-50 ${selectedService ? 'border-[#00529B]' : 'border-blue-200'} ${dropdownOpen ? 'border-[#00529B] shadow-md' : 'hover:border-blue-300'}`} data-id="element-3014">
                      <div className="flex items-center gap-3" data-id="element-3015">
                        {selectedService ? <>
                            <span className="text-[#00529B]" data-id="element-3016">
                              {services.find(s => s.id === selectedService)?.icon}
                            </span>
                            <span className="font-bold text-[#00529B] text-lg" data-id="element-3017">
                              {services.find(s => s.id === selectedService)?.name}
                            </span>
                          </> : <span className="text-gray-400 font-semibold text-lg" data-id="element-3018">
                            Choose a service...
                          </span>}
                      </div>
                      <ChevronDownIcon className={`w-5 h-5 text-[#00529B] transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`} data-id="element-3019" />
                    </button>

                    <AnimatePresence data-id="element-3020">
                      {dropdownOpen && <motion.div initial={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} animate={{
                    opacity: 1,
                    y: 0,
                    scaleY: 1
                  }} exit={{
                    opacity: 0,
                    y: -8,
                    scaleY: 0.95
                  }} transition={{
                    duration: 0.15,
                    ease: 'easeOut'
                  }} className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl border-2 border-blue-200 shadow-xl z-30 overflow-hidden origin-top" data-id="element-3021">
                          {services.map(svc => <button key={svc.id} onClick={() => {
                      setSelectedService(svc.id);
                      setDropdownOpen(false);
                    }} className={`w-full flex items-center gap-3 px-5 py-4 text-left transition-colors duration-150 ${selectedService === svc.id ? 'bg-blue-50 text-[#00529B]' : 'bg-white text-gray-700 hover:bg-blue-50 hover:text-[#00529B]'} border-b border-blue-100 last:border-b-0`} data-id="element-3022">
                              <span className={`${selectedService === svc.id ? 'text-[#00529B]' : 'text-gray-400'}`} data-id="element-3023">
                                {svc.icon}
                              </span>
                              <span className="font-bold text-lg" data-id="element-3024">
                                {svc.name}
                              </span>
                              {selectedService === svc.id && <CheckCircle2Icon className="w-5 h-5 text-[#00529B] ml-auto" data-id="element-3025" />}
                            </button>)}
                        </motion.div>}
                    </AnimatePresence>
                  </div>}

                {initialService && <div className="flex items-center justify-center gap-2 mb-6 bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5" data-id="element-3026">
                    <span className="text-[#00529B]" data-id="element-3027">
                      {services.find(s => s.id === initialService)?.icon}
                    </span>
                    <span className="font-bold text-[#00529B]" data-id="element-3028">
                      {serviceLabel}
                    </span>
                  </div>}

                {/* Zip Input */}
                <div className="relative mb-6" data-id="element-3029">
                  <input type="text" value={zipCode} onChange={handleZipChange} placeholder="Zip Code" maxLength={5} className="w-full h-16 px-6 text-2xl font-bold text-center bg-white border-2 border-gray-300 rounded-xl text-gray-900 focus:outline-none focus:border-[#00529B] focus:ring-1 focus:ring-[#00529B] transition-colors" data-id="element-3030" />
                  {isValidZip && <div className="absolute right-4 top-1/2 -translate-y-1/2 text-green-500" data-id="element-3031">
                      <CheckCircle2Icon className="w-8 h-8" data-id="element-3032" />
                    </div>}
                </div>

                <button onClick={handleContinue} disabled={!isValidZip || !selectedService} className={`w-full h-16 rounded-xl font-bold text-xl text-white transition-all shadow-lg ${isValidZip && selectedService ? 'bg-[#00529B] hover:bg-[#004080] shadow-blue-900/30 hover:shadow-blue-900/40 cursor-pointer' : 'bg-gray-300 cursor-not-allowed shadow-none'}`} data-id="element-3033">
                  Get Free Quote
                </button>

                {/* Trust Badges */}
                <div className="mt-8 flex items-center justify-center gap-6" data-id="element-3034">
                  <div className="flex items-center gap-2 bg-yellow-50 px-3 py-1.5 rounded-lg border border-yellow-200" data-id="element-3035">
                    <ShieldCheckIcon className="w-5 h-5 text-yellow-600" data-id="element-3036" />
                    <span className="text-xs font-bold text-yellow-800 uppercase tracking-tight" data-id="element-3037">
                      Lifetime Warranty
                    </span>
                  </div>
                  <div className="flex items-center gap-2 bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-200" data-id="element-3038">
                    <div className="font-black text-[#00529B] text-sm" data-id="element-3039">BBB</div>
                    <span className="text-xs font-bold text-blue-800 uppercase tracking-tight" data-id="element-3040">
                      A+ Rating
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white" data-id="element-3041">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3042">
          <div className="text-center mb-16" data-id="element-3043">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4" data-id="element-3044">
              Why Choose Universal Windows Direct?
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto" data-id="element-3045">
              We provide top-quality products, expert installation, and
              industry-leading warranties to protect your investment.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-3046">
            {[{
            title: 'Exclusive UniShield® Products',
            desc: "Our proprietary UniShield® windows and siding offer superior energy efficiency and durability you won't find anywhere else.",
            icon: <StarIcon className="w-8 h-8 text-[#00529B]" data-id="element-3047" />
          }, {
            title: 'True Lifetime Warranty',
            desc: 'We stand behind our products and installation with an industry-leading True Lifetime Warranty for your total peace of mind.',
            icon: <ShieldCheckIcon className="w-8 h-8 text-[#00529B]" data-id="element-3048" />
          }, {
            title: 'Expert Installation',
            desc: 'Our factory-trained and certified installers ensure your new products perform flawlessly for decades to come.',
            icon: <CheckCircle2Icon className="w-8 h-8 text-[#00529B]" data-id="element-3049" />
          }].map((feature, idx) => <div key={idx} className="bg-gray-50 p-8 rounded-2xl border border-gray-100 text-center hover:shadow-md transition-shadow" data-id="element-3050">
                <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-6 shadow-sm" data-id="element-3051">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-3052">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-3053">{feature.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#002A54] py-16" data-id="element-3054">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3055">
          <div className="flex flex-col md:flex-row justify-between items-start gap-12 mb-16" data-id="element-3056">
            <div className="flex flex-col gap-8" data-id="element-3057">
              <div className="flex flex-col" data-id="element-3058">
                <span className="text-2xl md:text-3xl font-black text-white tracking-tight" data-id="element-3059">
                  UNIVERSAL WINDOWS DIRECT
                </span>
              </div>
              <p className="text-xs text-blue-200 font-medium" data-id="element-3060">
                &copy; {new Date().getFullYear()} findservicequotes.io. All
                Rights Reserved.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-12" data-id="element-3061">
              <div data-id="element-3062">
                <h4 className="text-white font-bold mb-4" data-id="element-3063">Products</h4>
                <ul className="space-y-2 text-sm text-blue-200" data-id="element-3064">
                  <li data-id="element-3065">Replacement Windows</li>
                  <li data-id="element-3066">Vinyl Siding</li>
                  <li data-id="element-3067">Gutter Protection</li>
                  <li data-id="element-3068">Entry Doors</li>
                </ul>
              </div>
              <div data-id="element-3069">
                <h4 className="text-white font-bold mb-4" data-id="element-3070">Legal</h4>
                <ul className="space-y-2 text-sm text-blue-200" data-id="element-3071">
                  <li data-id="element-3072">Privacy Policy</li>
                  <li data-id="element-3073">Terms of Service</li>
                  <li data-id="element-3074">Do Not Sell My Info</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}